import { HoverIntent } from '@/react/components/utility/HoverIntent';
import { classes } from '@/next-utils/css-utils/scss-utils';
import type { IColorVariationAttribute } from '@/services/models/Product/variation-attributes';
import { generateSwatchBackgroundStyles } from '@/react/utils/product-utils';
import { type CSSProperties, type FC, type MouseEventHandler } from 'react';
import type { VoidCallback } from '@/type-utils';
import LoggerService from '@/services/isomorphic/LoggerService';
import { msgf } from '@/services/isomorphic/I18NService';
import { isWhiteHexColor, type SwatchHexValue } from '@/utils/color-utils';
import { validateHexValue } from '../utils/validateHexValue';

import S from './styles.module.scss';
import { product_tiles_selectColor } from "@/lang/__generated__/ahnu/product_tiles_selectColor";

/**
 * Represents a swatch variation for the product tile.
 *
 * Extends IColorVariationAttribute with SKU for product identification.
 */
export interface ISwatchVariation extends IColorVariationAttribute {
  /**
   * The SKU of the product variation. This is used to identify the product variation.
   */
  sku: string;
}

interface ISwatchProps extends Omit<ISwatchVariation, 'sku'> {
  /** If the swatch is currently selected. */
  active?: boolean;
  /**
   * Callback to run on swatch click.
   *
   * @param event
   */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /** Callback to run on hover. */
  onHover?: VoidCallback;

  /** Mouse leave action. */
  onLeave?: VoidCallback;

  /** Accessible label for the swatch button. */
  'aria-label'?: string;

  /** Whether to skip validation of the hex value. */
  skipValidation?: boolean;
}

/**
 *  Get the background style object for the swatch from the swatch hex value.
 *
 *  @param hexValue - The hex value of the swatch.
 *  @returns The background style object, that can be a linear gradient or a single color.
 */
const getSwatchStyleObject = (hexValue: SwatchHexValue): CSSProperties => {
  if (Array.isArray(hexValue) && hexValue.length <= 1) {
    LoggerService.warn('Swatch hex array has less than 2 colors');
  }

  const style = generateSwatchBackgroundStyles(hexValue);

  // Add border for white backgrounds (handles #fff, #FFF, #ffffff, #FFFFFF)
  if (
    'backgroundColor' in style &&
    typeof style.backgroundColor === 'string' &&
    isWhiteHexColor(style.backgroundColor as `#${string}`)
  ) {
    return { ...style, border: '1px solid #000' };
  }

  return style;
};

/**
 * A swatch for variations in the the product tile.
 *
 * @throws {@link MissingHexValueError} When hexValue is null, undefined, or empty.
 * @throws {@link EmptyHexArrayError} When hexValue is an empty array.
 * @throws {@link InvalidHexColorError} When hexValue contains invalid hex color
 * format(s).
 *
 * All errors are caught by SwatchErrorBoundary to display an ErrorSwatch or
 * hide the swatch based on environment configuration.
 */
export const Swatch: FC<ISwatchProps> = ({
  active,
  hexValue,
  name,
  onHover,
  onClick,
  onLeave,
  'aria-label': ariaLabel,
  skipValidation = false
}) => {
  if (!skipValidation) {
    validateHexValue(hexValue);
  }

  return (
    <HoverIntent onHoverIn={onHover} onHoverOut={onLeave} delay={0}>
      <button
        className={S.button}
        type="button"
        onClick={onClick}
        aria-label={
          ariaLabel ?? msgf(product_tiles_selectColor, { colorName: name })
        }
      >
        <div
          className={classes(S.swatch, active && S.active)}
          style={getSwatchStyleObject(hexValue)}
        />
      </button>
    </HoverIntent>
  );
};
